import React, { useEffect, useState } from 'react';
import Loader from '../../../components/Common/Loader';
import BackButton from '../../../components/Common/BackButton';
import {
  getAvailableProjectTemplates,
  ProjectTemplateOption,
} from '../services/ProjectTemplate.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

interface ClassroomAddNewProjectProps {
  close?: () => void;
  back?: () => void;
  submit: (name: string, projectTemplateId?: string) => void;
  loader?: boolean;
  isCentered?: boolean;
  isOnboarding?: boolean;
}

const ClassroomAddNewProject: React.FC<ClassroomAddNewProjectProps> = ({
  close,
  back,
  submit,
  loader,
  isCentered,
  isOnboarding,
}: ClassroomAddNewProjectProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const [templates, setTemplates] = useState<ProjectTemplateOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    setLoadingTemplates(true);
    getAvailableProjectTemplates()
      .then((data) => {
        setTemplates(data);

        const leadProjectId = user?.lead?.projectId;
        if (isOnboarding && leadProjectId) {
          const match = data.find(
            (t) => String(t.id) === String(leadProjectId) || String(t.wpProjectId) === String(leadProjectId),
          );
          if (match) {
            setSelectedTemplateId(match.id);
          }
        }
      })
      .finally(() => setLoadingTemplates(false));
  }, [isOnboarding, user?.lead?.projectId]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const projectDisplayName = selectedTemplate ? selectedTemplate.wpTitle || selectedTemplate.name : '';

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleSubmit = () => {
    if (!selectedTemplateId || !projectDisplayName) return;
    submit(projectDisplayName, selectedTemplateId);
  };

  const canSubmit = Boolean(selectedTemplateId && projectDisplayName?.length && !loader);

  return (
    <div className={`flex flex-col text-black font-sofiaSans mt-4 ${isCentered ? 'items-center' : 'items-start'}`}>
      <label htmlFor="template" className="block mb-2 font-bold text-black">
        Project
      </label>
      <div className="w-full border border-gray-300 rounded p-2 mb-2">
        <select
          id="template"
          name="template"
          className="w-full outline-none bg-transparent cursor-pointer"
          value={selectedTemplateId}
          onChange={(e) => handleTemplateChange(e.target.value)}
          disabled={loadingTemplates}
        >
          <option value="">{loadingTemplates ? 'Loading projects...' : 'Select a project'}</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {selectedTemplate?.wpDateList && (
        <div className="w-full mb-3 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
          <p className="text-sm font-semibold text-storyblue">{selectedTemplate.wpDateList}</p>
        </div>
      )}

      {selectedTemplate && (
        <div className="w-full mb-4">
          {selectedTemplate.wpExcerpt && (
            <p className="text-gray-600 text-sm mt-1 mb-2">{selectedTemplate.wpExcerpt}</p>
          )}
          {selectedTemplate.wpLink && (
            <a
              href={selectedTemplate.wpLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondaryStoryAdditionalLightBlueText hover:underline text-sm font-medium"
            >
              Preview {selectedTemplate.wpTitle || selectedTemplate.name} →
            </a>
          )}
        </div>
      )}

      <div className={`flex w-full ${isOnboarding ? 'justify-between' : 'justify-end'} gap-2 mt-1`}>
        {isOnboarding && back && <BackButton onClick={back}></BackButton>}
        {!isOnboarding && (
          <button
            onClick={close}
            className="bg-transparent hover:bg-[#000000] hover:bg-opacity-20 text-black font-semibold py-2 px-4 rounded-full focus:outline-none focus:ring-1 w-auto flex items-center"
          >
            Cancel
          </button>
        )}
        <button
          className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center ${
            canSubmit ? '' : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Create
          <Loader loader={loader} dimentions="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default ClassroomAddNewProject;
